import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams, useParams } from 'react-router-dom';
import {
  User, Phone, Lock, Eye, EyeOff, AlertCircle, CheckCircle,
  MessageCircle, Loader2, ChevronLeft,
} from 'lucide-react';
import { API_ENDPOINTS } from '../config/api';
import SEOHead from '../components/SEOHead';

const INDIAN_PREFIX = '+91';
const MOBILE_LENGTH = 10;
const OTP_LENGTH = 6;
const MIN_PASSWORD = 8;

const getPasswordStrength = (password) => {
  if (!password) return { score: 0, label: '', color: '' };
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^a-zA-Z0-9]/.test(password)) score++;
  if (score <= 1) return { score, label: 'Weak', color: '#dc2626' };
  if (score <= 3) return { score, label: 'Fair', color: '#d97706' };
  return { score, label: 'Strong', color: '#16a34a' };
};

const BrandMark = ({ height = 40, maxWidth = 180, className = '' }) => (
  <img
    src="/images/reddyannabook.png"
    alt="REDDY ANNA BOOK"
    className={`w-auto object-contain ${className}`}
    style={{ height, maxWidth }}
  />
);

const FieldLabel = ({ children, htmlFor }) => (
  <label
    htmlFor={htmlFor}
    style={{
      display: 'block',
      fontSize: 13,
      fontWeight: 600,
      color: '#d1d5db',
      marginBottom: 6,
    }}
  >
    {children}
  </label>
);

const Alert = ({ type = 'error', children }) => {
  const cfg = type === 'success'
    ? { bg: 'rgba(16,185,129,0.1)', border: 'rgba(16,185,129,0.3)', color: '#34d399', Icon: CheckCircle }
    : type === 'warning'
      ? { bg: 'rgba(245,158,11,0.1)', border: 'rgba(245,158,11,0.3)', color: '#fbbf24', Icon: AlertCircle }
      : { bg: 'rgba(220,38,38,0.1)', border: 'rgba(220,38,38,0.3)', color: '#f87171', Icon: AlertCircle };
  const { Icon } = cfg;
  return (
    <div
      className="flex items-start gap-2"
      style={{
        background: cfg.bg,
        border: `1px solid ${cfg.border}`,
        color: cfg.color,
        borderRadius: 8,
        padding: 12,
        fontSize: 13,
      }}
    >
      <Icon className="w-4 h-4 shrink-0 mt-0.5" />
      <span>{children}</span>
    </div>
  );
};

const RegisterPage = ({ setUser }) => {
  const [step, setStep] = useState('form'); // 'form' | 'otp'
  const [signupData, setSignupData] = useState({
    username: '',
    mobileDigits: '',
    password: '',
    referralCode: '',
    termsAccepted: true,
  });
  const [otp, setOtp] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const otpTimerRef = useRef(null);
  const [referralValid, setReferralValid] = useState(null);
  const [generalError, setGeneralError] = useState('');
  const otpInputsRef = useRef([]);
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const { referralCode: referralCodeParam } = useParams();

  // Auto-capture referral code from URL (?ref=XXX or /register/:referralCode)
  // No UI — user never sees this field.
  useEffect(() => {
    const refFromUrl = referralCodeParam || searchParams.get('ref');
    if (refFromUrl && !signupData.referralCode) {
      setSignupData((prev) => ({ ...prev, referralCode: refFromUrl }));
      validateReferralCode(refFromUrl);
    }
  }, [searchParams, referralCodeParam]);

  const fullMobile = INDIAN_PREFIX + (signupData.mobileDigits || '').replace(/\D/g, '');
  const passwordStrength = getPasswordStrength(signupData.password);
  const mobileDigitsOnly = (signupData.mobileDigits || '').replace(/\D/g, '').slice(0, MOBILE_LENGTH);

  // Silent referral validation — captured from URL, never shown in UI
  const validateReferralCode = async (code) => {
    if (!code || !code.trim()) {
      setReferralValid(null);
      return;
    }
    try {
      const response = await fetch(API_ENDPOINTS.VALIDATE_REFERRAL_CODE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ referral_code: code.trim() }),
      });
      const data = await response.json();
      setReferralValid(!!(response.ok && data.success));
    } catch (_) {
      setReferralValid(false);
    }
  };

  const startOtpTimer = () => {
    setOtpTimer(60);
    if (otpTimerRef.current) clearInterval(otpTimerRef.current);
    otpTimerRef.current = setInterval(() => {
      setOtpTimer((prev) => {
        if (prev <= 1) {
          clearInterval(otpTimerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResendOtp = async () => {
    setGeneralError('');
    try {
      const res = await fetch(API_ENDPOINTS.SEND_OTP, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_number: fullMobile, username: signupData.username.trim() }),
      });
      const data = await res.json();
      if (data.success) {
        startOtpTimer();
        setGeneralError('');
      } else {
        setGeneralError(data.message || 'Failed to resend OTP');
      }
    } catch {
      setGeneralError('Network error. Please try again.');
    }
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setGeneralError('');
    if (!signupData.username.trim()) {
      setGeneralError('Please enter a username');
      return;
    }
    if (mobileDigitsOnly.length !== MOBILE_LENGTH) {
      setGeneralError('Please enter a valid 10-digit Indian mobile number');
      return;
    }
    if (!['6', '7', '8', '9'].includes(mobileDigitsOnly[0])) {
      setGeneralError('Indian mobile numbers must start with 6, 7, 8, or 9');
      return;
    }
    if (/^(\d)\1{9}$/.test(mobileDigitsOnly)) {
      setGeneralError('Please enter a real phone number, not repeated digits');
      return;
    }
    if (/(\d)\1{5,}/.test(mobileDigitsOnly)) {
      setGeneralError('Please enter a valid phone number');
      return;
    }
    if (['1234567890', '9876543210', '0123456789'].includes(mobileDigitsOnly)) {
      setGeneralError('Please enter a valid phone number');
      return;
    }
    if (signupData.password.length < MIN_PASSWORD) {
      setGeneralError(`Password must be at least ${MIN_PASSWORD} characters`);
      return;
    }
    if (!signupData.termsAccepted) {
      setGeneralError('Please accept the Terms & Conditions to continue');
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.SEND_OTP, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone_number: fullMobile,
          username: signupData.username.trim(),
        }),
      });
      const rawText = await response.text();
      let data = {};
      try {
        if (rawText && rawText.trim()) data = JSON.parse(rawText);
      } catch (_) {
        setGeneralError(
          response.status === 404 || rawText.toLowerCase().includes('not found')
            ? 'Backend not reached. Please try again later or contact support.'
            : 'Invalid response from server. Please try again.'
        );
        setIsLoading(false);
        return;
      }
      if (response.ok) {
        setStep('otp');
        setOtp('');
        setGeneralError('');
        startOtpTimer();
      } else if (response.status === 429) {
        setGeneralError('Too many requests. Please wait a few minutes and try again.');
      } else {
        setGeneralError(data.message || data.error || 'Failed to send OTP');
      }
    } catch (err) {
      const msg = err?.message || String(err);
      setGeneralError(`Network error: ${msg}. Please check your connection and try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyAndSignUp = async (e) => {
    e.preventDefault();
    setGeneralError('');
    const otpDigits = otp.replace(/\D/g, '');
    if (otpDigits.length !== OTP_LENGTH) {
      setGeneralError(`Please enter the ${OTP_LENGTH}-digit OTP`);
      return;
    }
    setIsLoading(true);
    try {
      const referralCode = signupData.referralCode.trim();
      const response = await fetch(API_ENDPOINTS.REGISTER, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: signupData.username.trim(),
          phone_number: fullMobile,
          password: signupData.password,
          display_name: signupData.username.trim(),
          referral_code: referralCode && referralValid === true ? referralCode : '',
          user_referral_code: signupData.referralCode?.trim() || '',
          otp: otpDigits,
        }),
      });
      let data;
      try {
        data = await response.json();
      } catch (_) {
        setGeneralError('Invalid response from server.');
        setIsLoading(false);
        return;
      }
      if (response.ok) {
        const user = data.data?.user ?? data.user;
        const token = data.data?.token ?? data.token;
        if (user && setUser) {
          try {
            localStorage.setItem('reddyannabook_user', JSON.stringify(user));
            if (token) localStorage.setItem('reddyannabook_token', token);
          } catch (_) {}
          setUser(user);
          navigate('/', { replace: true });
        } else {
          setGeneralError('Sign up succeeded but no user data received.');
        }
      } else {
        setGeneralError(data.message || data.error || 'Invalid OTP or sign up failed');
      }
    } catch (err) {
      const msg = err?.message || String(err);
      setGeneralError(`Network error: ${msg}. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  const updateMobileDigits = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, MOBILE_LENGTH);
    setSignupData((prev) => ({ ...prev, mobileDigits: digits }));
  };

  // OTP: 6-box input handling
  const updateOtpDigit = (idx, ch) => {
    const clean = (ch || '').replace(/\D/g, '').slice(0, 1);
    const chars = otp.replace(/\D/g, '').padEnd(OTP_LENGTH, ' ').split('').slice(0, OTP_LENGTH);
    chars[idx] = clean || ' ';
    const joined = chars.join('').replace(/\s/g, '');
    setOtp(joined);
    if (clean && idx < OTP_LENGTH - 1) {
      otpInputsRef.current[idx + 1]?.focus();
    }
  };
  const handleOtpKey = (idx, e) => {
    if (e.key === 'Backspace') {
      const chars = otp.split('');
      if (!chars[idx] && idx > 0) {
        otpInputsRef.current[idx - 1]?.focus();
      }
    }
  };
  const handleOtpPaste = (e) => {
    const pasted = (e.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (pasted.length) {
      e.preventDefault();
      setOtp(pasted);
      otpInputsRef.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
    }
  };

  // -----------------------------------------------------------
  // Shared left hero panel (desktop only)
  // -----------------------------------------------------------
  const HeroPanel = () => (
    <div
      className="hidden lg:flex flex-col justify-center"
      style={{
        background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #0a0a0a 100%)',
        padding: 48,
        color: '#ffffff',
      }}
    >
      <BrandMark height={64} maxWidth={240} />
      <p
        className="font-semibold"
        style={{ color: '#ffffff', fontSize: 20, marginTop: 16, lineHeight: 1.3 }}
      >
        Join India's Most Trusted Betting Exchange
      </p>
      <p style={{ color: '#9ca3af', fontSize: 14, marginTop: 8 }}>
        Get instant ID • 24/7 Support • Secure Payments
      </p>

      <ul className="list-none p-0 m-0" style={{ marginTop: 40 }}>
        {[
          'Welcome Bonus up to ₹50,000',
          'Instant Deposits & Withdrawals',
          'Live Cricket, IPL, Casino & More',
          '24/7 WhatsApp Support',
        ].map((text) => (
          <li
            key={text}
            className="flex items-center"
            style={{ gap: 12, marginBottom: 14 }}
          >
            <CheckCircle className="w-5 h-5 shrink-0" style={{ color: '#f59e0b' }} />
            <span style={{ color: '#ffffff', fontSize: 14, fontWeight: 500 }}>
              {text}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );

  const BackButton = () => (
    <button
      type="button"
      onClick={() => {
        if (step === 'otp') {
          setStep('form');
          if (otpTimerRef.current) clearInterval(otpTimerRef.current);
        } else {
          navigate('/');
        }
      }}
      className="inline-flex items-center gap-1 hover:text-white"
      style={{ color: '#9ca3af', fontSize: 13, fontWeight: 500, background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}
    >
      <ChevronLeft className="w-4 h-4" />
      Back
    </button>
  );

  // Reusable input style (icon + border focus via className)
  const inputClass = 'kabook-reg-input-dark';
  const inputStyle = {
    width: '100%',
    background: '#0a0a0a',
    border: '1.5px solid #2a2a2a',
    borderRadius: 10,
    padding: '14px 16px 14px 44px',
    fontSize: 14,
    color: '#ffffff',
    outline: 'none',
  };
  const iconStyle = {
    position: 'absolute',
    left: 14,
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#9ca3af',
    pointerEvents: 'none',
  };

  // ===========================================================
  // FORM STEP
  // ===========================================================
  const FormStep = () => (
    <form onSubmit={handleSendOtp} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Username */}
      <div>
        <FieldLabel htmlFor="kb-username">Username</FieldLabel>
        <div style={{ position: 'relative' }}>
          <User size={18} style={iconStyle} />
          <input
            id="kb-username"
            className={inputClass}
            style={inputStyle}
            type="text"
            autoComplete="username"
            value={signupData.username}
            onChange={(e) => setSignupData((prev) => ({ ...prev, username: e.target.value }))}
            placeholder="Choose a username"
            required
          />
        </div>
      </div>

      {/* Phone number with +91 prefix */}
      <div>
        <FieldLabel htmlFor="kb-mobile">Phone Number</FieldLabel>
        <div
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'stretch',
          }}
        >
          <div
            className="flex items-center justify-center font-semibold"
            style={{
              background: '#0a0a0a',
              border: '1.5px solid #2a2a2a',
              borderRadius: 10,
              padding: '0 14px',
              fontSize: 14,
              color: '#d1d5db',
              whiteSpace: 'nowrap',
            }}
          >
            <span aria-hidden style={{ marginRight: 6 }}>🇮🇳</span>
            {INDIAN_PREFIX}
          </div>
          <div style={{ position: 'relative', flex: 1 }}>
            <Phone size={18} style={iconStyle} />
            <input
              id="kb-mobile"
              className={inputClass}
              style={inputStyle}
              type="tel"
              inputMode="numeric"
              autoComplete="tel"
              value={signupData.mobileDigits}
              onChange={(e) => updateMobileDigits(e.target.value)}
              placeholder="Enter your 10-digit mobile number"
              maxLength={MOBILE_LENGTH}
              required
            />
          </div>
        </div>
        <p style={{ color: '#34d399', fontSize: 12, marginTop: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
          <MessageCircle className="w-3 h-3" /> You will receive an OTP on WhatsApp
        </p>
      </div>

      {/* Password */}
      <div>
        <FieldLabel htmlFor="kb-password">Set Password</FieldLabel>
        <div style={{ position: 'relative' }}>
          <Lock size={18} style={iconStyle} />
          <input
            id="kb-password"
            className={inputClass}
            style={{ ...inputStyle, paddingRight: 44 }}
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            value={signupData.password}
            onChange={(e) => setSignupData((prev) => ({ ...prev, password: e.target.value }))}
            placeholder="Create a strong password"
            minLength={MIN_PASSWORD}
            required
          />
          <button
            type="button"
            aria-label="Toggle password visibility"
            onClick={() => setShowPassword((s) => !s)}
            className="hover:text-white"
            style={{
              position: 'absolute',
              right: 14,
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#9ca3af',
              background: 'transparent',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
            }}
          >
            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
        {signupData.password ? (
          <p style={{ fontSize: 12, marginTop: 4, color: passwordStrength.color }}>
            Strength: {passwordStrength.label}
            {signupData.password.length < MIN_PASSWORD && ` (min ${MIN_PASSWORD} characters)`}
          </p>
        ) : (
          <p style={{ color: '#9ca3af', fontSize: 12, marginTop: 4 }}>
            Minimum {MIN_PASSWORD} characters
          </p>
        )}
      </div>

      {/* Terms */}
      <label
        className="flex items-start gap-2"
        style={{ cursor: 'pointer', fontSize: 13, color: '#d1d5db', marginTop: 4 }}
      >
        <input
          type="checkbox"
          checked={signupData.termsAccepted}
          onChange={(e) => setSignupData((prev) => ({ ...prev, termsAccepted: e.target.checked }))}
          className="kabook-reg-check-dark"
          style={{ marginTop: 2 }}
        />
        <span>
          I agree to the{' '}
          <Link to="/rules" style={{ color: '#fbbf24', fontWeight: 600 }} className="hover:underline">
            Terms &amp; Conditions
          </Link>{' '}
          and{' '}
          <Link to="/rules" style={{ color: '#fbbf24', fontWeight: 600 }} className="hover:underline">
            Privacy Policy
          </Link>
        </span>
      </label>

      {generalError && <Alert>{generalError}</Alert>}

      {/* Send OTP button */}
      <button
        type="submit"
        disabled={
          isLoading ||
          !signupData.termsAccepted ||
          (signupData.referralCode.trim() !== '' && referralValid === false)
        }
        className="kabook-otp-btn flex items-center justify-center gap-2"
        style={{
          height: 48,
          borderRadius: 10,
          background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
          color: '#0a0a0a',
          fontWeight: 700,
          fontSize: 15,
          border: 'none',
          boxShadow: '0 4px 12px rgba(245,158,11,0.3)',
          marginTop: 8,
        }}
      >
        {isLoading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" /> Sending OTP...
          </>
        ) : (
          <>
            <MessageCircle className="w-4 h-4" /> Send OTP via WhatsApp
            <span
              aria-hidden
              style={{
                width: 8,
                height: 8,
                borderRadius: 9999,
                background: '#25D366',
                boxShadow: '0 0 0 2px rgba(255,255,255,0.4)',
              }}
            />
          </>
        )}
      </button>
    </form>
  );

  // ===========================================================
  // OTP STEP
  // ===========================================================
  const OtpStep = () => (
    <form onSubmit={handleVerifyAndSignUp} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div
        style={{
          background: 'rgba(16,185,129,0.1)',
          border: '1px solid rgba(16,185,129,0.3)',
          borderRadius: 10,
          padding: 14,
          textAlign: 'center',
        }}
      >
        <div className="flex items-center justify-center gap-2 mb-1" style={{ color: '#34d399', fontWeight: 700 }}>
          <MessageCircle className="w-4 h-4" /> Check your WhatsApp!
        </div>
        <p style={{ color: '#a7f3d0', fontSize: 13 }}>We sent a 6-digit OTP to</p>
        <p style={{ color: '#ffffff', fontWeight: 700, fontSize: 15, marginTop: 2 }}>{fullMobile}</p>
      </div>

      <div>
        <FieldLabel>Enter OTP</FieldLabel>
        <div
          onPaste={handleOtpPaste}
          style={{ display: 'grid', gridTemplateColumns: `repeat(${OTP_LENGTH}, minmax(0, 1fr))`, gap: 8 }}
        >
          {Array.from({ length: OTP_LENGTH }).map((_, i) => {
            const val = otp[i] || '';
            return (
              <input
                key={i}
                ref={(el) => (otpInputsRef.current[i] = el)}
                type="text"
                inputMode="numeric"
                autoComplete={i === 0 ? 'one-time-code' : 'off'}
                maxLength={1}
                value={val}
                onChange={(e) => updateOtpDigit(i, e.target.value)}
                onKeyDown={(e) => handleOtpKey(i, e)}
                className="kabook-reg-input-dark"
                style={{
                  height: 48,
                  textAlign: 'center',
                  fontSize: 18,
                  fontWeight: 700,
                  borderRadius: 10,
                  border: '1.5px solid #2a2a2a',
                  background: '#0a0a0a',
                  color: '#ffffff',
                  outline: 'none',
                }}
              />
            );
          })}
        </div>
        <div style={{ textAlign: 'center', marginTop: 10, fontSize: 13, color: '#9ca3af' }}>
          {otpTimer > 0 ? (
            <>Resend OTP in <span style={{ color: '#fbbf24', fontWeight: 700 }}>{otpTimer}s</span></>
          ) : (
            <button
              type="button"
              onClick={handleResendOtp}
              style={{ color: '#fbbf24', fontWeight: 700, background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}
              className="hover:underline"
            >
              Resend OTP
            </button>
          )}
        </div>
      </div>

      {generalError && (
        <Alert type={generalError.startsWith('OTP for testing') ? 'warning' : 'error'}>
          {generalError}
        </Alert>
      )}

      <button
        type="submit"
        disabled={isLoading || otp.replace(/\D/g, '').length !== OTP_LENGTH}
        className="kabook-otp-btn flex items-center justify-center gap-2"
        style={{
          height: 52,
          borderRadius: 10,
          background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
          color: '#0a0a0a',
          fontWeight: 700,
          fontSize: 16,
          border: 'none',
          boxShadow: '0 4px 12px rgba(245,158,11,0.3)',
        }}
      >
        {isLoading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" /> Creating your account...
          </>
        ) : (
          'Create Account'
        )}
      </button>
    </form>
  );

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5" style={{ background: '#0a0a0a' }}>
      <SEOHead
        title="Reddy Anna Book Register – Create Your Betting Account | Get Instant ID"
        description="Register on Reddy Anna Book in 30 seconds via WhatsApp OTP. Get instant ID for cricket betting, IPL, casino games. Safe, secure, and trusted by thousands in India."
        keywords="reddyannabook register, reddyannabook signup, create betting account, get betting id india"
        path="/register"
      />
      {/* Left hero — desktop only (2/5) */}
      <div className="lg:col-span-2">
        {HeroPanel()}
      </div>

      {/* Right form section (3/5) — dark */}
      <div className="lg:col-span-3 flex flex-col justify-center" style={{ background: '#0a0a0a', padding: 24 }}>
        <div
          style={{
            width: '100%',
            maxWidth: 440,
            margin: '0 auto',
            background: '#1a1a1a',
            border: '1px solid #2a2a2a',
            borderRadius: 16,
            padding: 24,
            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
          }}
          className="lg:p-10"
        >
          {/* Back button */}
          <div style={{ marginBottom: 16 }}>
            {BackButton()}
          </div>

          {/* Mobile-only logo */}
          <div className="lg:hidden flex justify-center" style={{ marginBottom: 24 }}>
            <BrandMark height={44} maxWidth={180} className="mx-auto" />
          </div>

          {/* Heading */}
          <h1 style={{ fontSize: 28, fontWeight: 700, color: '#ffffff', lineHeight: 1.2 }}>
            {step === 'otp' ? 'Verify Your Number' : 'Create Your Account'}
          </h1>
          <p style={{ color: '#9ca3af', fontSize: 14, marginTop: 8, marginBottom: 28 }}>
            {step === 'otp'
              ? 'Enter the 6-digit code we sent to your WhatsApp'
              : 'Register in seconds and start playing'}
          </p>

          {step === 'otp' ? OtpStep() : FormStep()}

          {/* Already have account */}
          <div style={{ textAlign: 'center', marginTop: 24, fontSize: 14, color: '#9ca3af' }}>
            Already have an account?{' '}
            <Link to="/login" style={{ color: '#fbbf24', fontWeight: 700 }} className="hover:underline">
              Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
